import {
  fmtUsd,
  normalizeTicker,
  resolveShareAvgCostForTicker,
  shareAvgFromStock,
  type StockPosition,
  type WheelRecommendation,
} from "./watchlistData";

export interface ProfitabilityResult {
  pct?: number;
  tip: string;
}

export function parseUsd(text: string | null | undefined): number | undefined {
  if (!text) return undefined;
  const cleaned = text.replace(/[$,%\s]/g, "").replace(/,/g, "");
  const value = Number.parseFloat(cleaned);
  return Number.isFinite(value) ? value : undefined;
}

export function fmtProfitPct(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(2)}%`;
}

/** Put: premium $ ÷ cash collateral × 100. */
export function putProfitability(
  lastPrice: number | undefined,
  collateral: number | undefined,
): ProfitabilityResult {
  if (lastPrice == null || collateral == null || collateral <= 0) {
    return {
      tip: "Premium yield on cash collateral: (last option price × 100) ÷ collateral. Requires last price and collateral.",
    };
  }

  const premium = lastPrice * 100;
  const pct = (premium / collateral) * 100;
  return {
    pct,
    tip:
      `Premium yield on cash collateral: (${fmtUsd(lastPrice)} × 100) ÷ ${fmtUsd(collateral)} ` +
      `= ${fmtProfitPct(pct)}. Based on the last traded option price; your fill may differ.`,
  };
}

/** Call: last option price ÷ share purchase price × 100. */
export function callProfitability(
  lastPrice: number | undefined,
  avgCost: number | undefined,
): ProfitabilityResult {
  if (lastPrice == null || avgCost == null || avgCost <= 0) {
    return {
      tip: "Premium yield on share cost: last option price ÷ your average share cost. Requires last price and avg cost.",
    };
  }

  const pct = (lastPrice / avgCost) * 100;
  return {
    pct,
    tip:
      `Premium yield on share cost: ${fmtUsd(lastPrice)} ÷ ${fmtUsd(avgCost)} avg cost ` +
      `= ${fmtProfitPct(pct)}. Based on the last traded option price; your fill may differ.`,
  };
}

export function isCallSide(side: string | undefined): boolean {
  const s = (side ?? "").toLowerCase();
  return s === "call" || s === "c";
}

export function recommendationProfitability(
  rec: WheelRecommendation,
  stock?: StockPosition,
  stockAvgs?: Map<string, number>,
): ProfitabilityResult {
  const last = rec.last_price;
  if (isCallSide(rec.side)) {
    const ticker = normalizeTicker(rec.underlying_ticker);
    const avg =
      stockAvgs?.size
        ? resolveShareAvgCostForTicker(ticker, stockAvgs, rec, stock)
        : rec.holding_avg_cost ?? (stock ? shareAvgFromStock(stock) : undefined);
    return callProfitability(last, avg);
  }
  return putProfitability(last, rec.collateral);
}

export function recommendationProfitPct(
  rec: WheelRecommendation,
  stock?: StockPosition,
  stockAvgs?: Map<string, number>,
): number | undefined {
  return recommendationProfitability(rec, stock, stockAvgs).pct;
}

/** Premium dollars earned per calendar day for one contract: premium × 100 ÷ DTE. */
export function recommendationPremiumPerDay(rec: WheelRecommendation): number | undefined {
  const mid = Number(rec.mid);
  const premium = mid > 0 ? mid : Number(rec.last_price);
  const dte = Number(rec.dte);
  if (!Number.isFinite(premium) || premium <= 0) return undefined;
  if (!Number.isFinite(dte) || dte <= 0) return undefined;
  return (premium * 100) / dte;
}

export function fmtPremiumPerDay(rec: WheelRecommendation): string {
  const value = recommendationPremiumPerDay(rec);
  return value == null ? "—" : `${fmtUsd(value)}/d`;
}

/** Trade profitability % ÷ DTE (premium yield on collateral per day). */
export function recommendationProfitPerDayPct(
  rec: WheelRecommendation,
  stock?: StockPosition,
  stockAvgs?: Map<string, number>,
): number | undefined {
  const profit = recommendationProfitPct(rec, stock, stockAvgs);
  const dte = Number(rec.dte);
  if (profit == null || !Number.isFinite(dte) || dte <= 0) return undefined;
  return profit / dte;
}
